import { Vector2 } from "three";
import { HSLColor } from "../../../library-js/colors/HSLColor";
import { ClassFlag } from "../../../library-js/dom-tools/ClassFlag";
import { ElementAttribute } from "../../../library-js/dom-tools/ElementAttribute";
import { ElementType } from "../../../library-js/dom-tools/ElementType";
import { OnChange } from "../../../library-js/dom-tools/EventListeners";
import { MathX } from "../../../library-js/math/MathX";
import { RegExpUtility } from "../../../library-js/text/RegExpUtility";
import { VerticalSlider, VerticalSliderElement } from "../../../library-templates/sliders/VerticalSlider";
import { ValueSlider } from "../../../library-templates/sliders/ValueSlider";
import { Plot } from "./Plot";
import { PlotLineSettings, PlotSettings } from "./PlotSettings";
import { Arrays } from "../../../library-js/tools/Arrays";
import { Biquad, BiquadType } from "../../../library/ts/vanilla/math/Biquad";
import { Rokojori } from "../Rokojori";
import { HTMLEditor } from "../../../library-js/dom-tools/HTMLEditor";


export class TimeVaryingSmoothing
{
  static numSamples = 1024;
  static readonly type = new ElementType( "time-varying-smoothing" );
  static readonly setterValuesInput = new ClassFlag( "setter-values" );
  static readonly coefficient = new ClassFlag( "coefficient" );
  static readonly coefficientPower = new ClassFlag( "coefficient-power" );
  static readonly biquadParameter = new ClassFlag( "biquad-parameter" );
  static readonly experimentalParameter = new ClassFlag( "experimental-parameter" );

  static readonly topCurveType = new ClassFlag( "top-curve" );
  static readonly bottomCurveType = new ClassFlag( "bottom-curve" );

  static applyOnDocument()
  {
    TimeVaryingSmoothing.type.forAllInDoc( e => this.applyOnElement( e ) );
  }

  static mapCoefficient( normalized:number, power:number )
  {
    return Math.pow( normalized, power );
  }

  static applyOnElement( e:Element ) 
  {
    let setterValuesInput = TimeVaryingSmoothing.setterValuesInput.query( e ) as HTMLInputElement;

    let coefficientPower = 1;
    let coefficient = this.mapCoefficient( 0.1, coefficientPower );
    let biquadParameterValue = 0.5;
    let experimentalParameterValue = 0.5;


    let updateAll = ()=>{};

    let coefficientPowerSlider = TimeVaryingSmoothing.coefficientPower.query( e );
    let coefficientValueSlider = TimeVaryingSmoothing.coefficient.query( e );
    let biquadParameter = TimeVaryingSmoothing.biquadParameter.query( e );
    let experimentalParameter = TimeVaryingSmoothing.experimentalParameter.query( e );

    ValueSlider.setup( coefficientPowerSlider, 
      ( normalizedValue:number ) =>
      {
        coefficientPower = MathX.map( normalizedValue, 0, 1, 1, 10 );
        ValueSlider.updateValue( coefficientValueSlider );
        return "pow( c, " + coefficientPower.toFixed( 3 ) + " )";
      } 
    );

    
    ValueSlider.setup( coefficientValueSlider, 
      ( normalizedValue:number ) =>
      {        
        coefficient = this.mapCoefficient( normalizedValue, coefficientPower );
        return coefficient.toFixed( 5 );
      } 
    );

    ValueSlider.setup( biquadParameter, 
      ( normalizedValue:number ) =>
      {        
        biquadParameterValue = Math.pow( 10,  MathX.map( normalizedValue, 0, 1, -1, 1 ) ); ;
        return biquadParameterValue .toFixed( 2 ) + " octaves" ;
      } 
    );

    ValueSlider.setup( experimentalParameter, 
      ( normalizedValue:number ) =>
      {        
        experimentalParameterValue = Math.pow( 2, MathX.map( normalizedValue, 0, 1, -3, 3 ) ); ;
        return  ( ( 100 * experimentalParameterValue ) .toFixed( 2 ) + "%" ) ;
      } 
    );

    ValueSlider.setValue( coefficientPowerSlider, 0.3 )
    ValueSlider.setValue( coefficientValueSlider, 0.5 );
    ValueSlider.setValue( biquadParameter, 0.68 );
    ValueSlider.setValue( experimentalParameter, 0.57 );
    ;

    let plotSettings = new PlotSettings();    
    plotSettings.min = new Vector2( 0, 1.5 );
    plotSettings.max = new Vector2( 1, -1.5 );


    let topPlot = Plot.create( 1024, 200 );
    topPlot._settings = plotSettings;

    let bottomPlot = Plot.create( 1024, 200 );
    bottomPlot._settings = plotSettings;

    let rates = [ 1, 0.5, 2 ];


    plotSettings.addLineSetting( new HSLColor( 10, 50, 50, 0.5 ) );

    for ( let i = 0; i < rates.length; i++ )
    {
      let isOne = rates[ i ] == 1;

      let hueOffset = i * 30;
      
      let alpha = isOne ? 1 : 0.6;
      let saturation = isOne ? 100 : 50;
      let luminance = isOne ? 80 : 50;
      let lineWidth = isOne ? 4 : 2;

      plotSettings.addLineSetting( new HSLColor( 170 + hueOffset, saturation, luminance, alpha ), lineWidth );

    }

    Rokojori.$.onAnimationFrame.addListener( 
      ()=>
      {
        let time = new Date().getTime();
        let duration = 10000;
        let phase = ( time % duration ) / duration;
        plotSettings.visualizePoint = phase;
        updateAll();
      }
    );

    let computeValuesByType = ( type:string, rate:number )=>
    {
      let setterValues = RegExpUtility.parseNumbers( setterValuesInput.value );

      switch ( type )
      {
        case "Time-Invariant Lerp":
        {
          return this.computeSmoothValueTimeInvariant( setterValues, coefficient, rate );
        }

        case "Exp":
        {
          return this.computeSmoothValuesExp( setterValues, coefficient, rate );
        }

        case "Pow":
        {
          return this.computeSmoothValuesPow( setterValues, coefficient, rate );
        }

        case "Time-Variant Lerp":
        {
          return this.computeSmoothValueTimeVariant( setterValues, coefficient, rate );
        }

        case "Biquad Lowpass":
        {
          let filter = Biquad.create( BiquadType.LowPass, 0, coefficient * 10000 + 20, biquadParameterValue, 44100 );
          return this.computeBiquad( setterValues, filter, rate );
        }

        case "Lerped Acceleration":
        {
          return this.computeRepeatWithAccelerationSmoothing( setterValues, coefficient, experimentalParameterValue, rate );
        }
      }
      
    };

    updateAll = ()=>
    {      
      let setterValues = RegExpUtility.parseNumbers( setterValuesInput.value );
      
      let hold  = this.computeHoldValues( setterValues, coefficient );


      let topType = TimeVaryingSmoothing.topCurveType.query( e ) as HTMLSelectElement;
      topPlot._plots = [ hold ];
      for ( let i = 0; i < rates.length; i++ )
      {
        let values = computeValuesByType( topType.value, rates[ i ] );
        topPlot._plots.push( values );
      }
      topPlot.render();


      let bottomType = TimeVaryingSmoothing.bottomCurveType.query( e ) as HTMLSelectElement;
      bottomPlot._plots = [ hold ];
      for ( let i = 0; i < rates.length; i++ )
      {
        let values = computeValuesByType( bottomType.value, rates[ i ] );
        bottomPlot._plots.push( values );
      }
      bottomPlot.render();

    }

    HTMLEditor.insertAfter( e, TimeVaryingSmoothing.topCurveType.query( e ), topPlot._element );
    HTMLEditor.insertAfter( e, TimeVaryingSmoothing.bottomCurveType.query( e ), bottomPlot._element );

    HTMLEditor.insertBreakBefore( topPlot._element );
    HTMLEditor.insertBreakBefore( bottomPlot._element  );
    
  }

  static computeValues( nextValueInterval:number, size:number, setterValues:number[], process:( before:number, next:number, time:number ) => number )
  {
    nextValueInterval = Math.round( nextValueInterval );
    size = Math.round( size );

    let values:number[] = [];
    
    let valuesIndex = 0;
    let nextValue = 0;
    let value = 0;

    values.push( value );
 
    let length = size - 1;

    let timeDuration = TimeVaryingSmoothing.numSamples/length;

    
    for ( let i = 0; i < length; i++ )
    {
      if ( i % nextValueInterval == 0 )
      {
        nextValue = setterValues[ valuesIndex ];
        valuesIndex = ( valuesIndex + 1 ) % setterValues.length;
      }

      value = process( value, nextValue, timeDuration );
      values.push( value );
    }

    return values;
  }

  static lerp( a:number, b:number, t:number )
  {
    return a + t * ( b - a );
  }

  static computeHoldValues( setterValues:number[], coefficient:number )
  {
    return this.computeValues( 100, TimeVaryingSmoothing.numSamples, setterValues, ( x, n ) => n ); 
  }

  static computeSmoothValueTimeInvariant( setterValues:number[], coefficient:number, rate:number )
  {
    return this.computeValues( 100 * rate, TimeVaryingSmoothing.numSamples * rate, setterValues, ( x, n ) => this.lerp( x, n, coefficient ) ); 
  }

  static computeElapsedTime( setterValues:number[], coefficient:number, rate:number )
  {
    let realElapsed = 0;
    
    let values = this.computeValues( 100 * rate, TimeVaryingSmoothing.numSamples * rate, setterValues, 
      ( x, n, t ) => 
      {
        realElapsed += t;

        return ( realElapsed / TimeVaryingSmoothing.numSamples );        
      }
      
    );  

    return values;
  }

  static computeSmoothValueTimeVariant( setterValues:number[], coefficient:number, rate:number )
  {
    let values = this.computeAnySmoothValueTimeVariant( setterValues, 0, rate,
      ( old, current ) => 
      {
        return this.lerp( old, current, coefficient );
      }
    );

    return values;
  }

  static computeRepeatWithAccelerationSmoothing( setterValues:number[], coefficient:number, coefficient2:number, rate:number )
  {
    let acc = 0;
    let values = this.computeAnySmoothValueTimeVariant( setterValues, 0, rate,
      ( old, current ) => 
      {
        let d = current - old;
        acc = this.lerp( acc, d, coefficient );
        
        let nextAcc = this.lerp( acc, 0, Math.pow( coefficient, coefficient2 ) );
        acc = this.lerp( nextAcc, acc, coefficient );
        let next = old + acc;

        return this.lerp( old, next, coefficient ); //this.lerp( old, current, coefficient );
      }
    );
    return values;
  }

  static computeAnySmoothValueTimeVariant( setterValues:number[], coefficient:number, rate:number, process:(x:number, n:number)=>number )
  {
    let overflow = 0;
    let timeStep = 0.5;

    let values = this.computeValues( 100 * rate, TimeVaryingSmoothing.numSamples * rate, setterValues, 
      ( x, n, t ) => 
      {
        let pt = t + overflow;
        
        while ( pt > 0 )
        {
          x = process( x, n );
          pt -= timeStep;
        }

        overflow = pt;

        return x;        
      }
      
    );    

    return values;
  }

  static computeBiquad( setterValues:number[], filter:Biquad, rate:number )
  {
    return this.computeAnySmoothValueTimeVariant( setterValues, 0, rate, ( old, current ) => filter.process( current ) );
  }

  

  static computeSmoothValuesExp( setterValues:number[], coefficient:number, rate:number )
  {

    let values = this.computeValues( 100 * rate, TimeVaryingSmoothing.numSamples * rate, setterValues, 
      ( x, n, t ) => 
      {       
        let lerpAmount = Math.exp( -coefficient * t );
        return this.lerp( n, x, lerpAmount );
      }
      
    );    

    return values;
  }
  

  static computeSmoothValuesPow( setterValues:number[], coefficient:number, rate:number )
  {
    let values = this.computeValues( 100 * rate, TimeVaryingSmoothing.numSamples * rate, setterValues, 
      ( x, n, t ) => 
      { 
        return this.lerp( x, n, 1 - Math.pow( 1 - coefficient, t ) );
      }
    ); 

    return values;
  }

  
}